import { CommonModule, DatePipe, isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import type {
  AiConsultantChatTranscriptCollection,
  AiConsultantCollection,
  AiConsultantMessage,
} from '@freelancer/datastore/collections';
import {
  AI_CONSULTANT_MESSAGE_EXCEPTION_ID,
  AI_CONSULTANT_MESSAGE_EXCEPTION_LIMIT_ID,
  AiConsultantDisplayMessageType,
  AiConsultantMessageRole,
  DatastoreAiConsultantChatTranscriptModule,
  DatastoreAiConsultantModule,
} from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { Tracking, TrackingModule } from '@freelancer/tracking';
import { ModalService, UiModule, trackByIndex } from '@freelancer/ui';
import { HrColor } from '@freelancer/ui/hr';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { InputSize, InputType } from '@freelancer/ui/input';
import { ModalSize } from '@freelancer/ui/modal';
import { ScrollableContentComponent } from '@freelancer/ui/scrollable-content';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { Resize } from '@freelancer/ui/textarea';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoginOrSignup } from 'app/login-signup/login-signup-modal';
import type { Observable } from 'rxjs';
import { firstValueFrom, from, map } from 'rxjs';
import type {
  AiConsultantMessageDraftProject,
  AiConsultantMessageLoading,
  AiConsultantMessageValid,
  AiConsultantProjectData,
  AiConsultantTrackingExtraParams,
} from '../ai-consultant.types';
import { SaveConversationToastError } from '../ai-consultant.types';
import { AiConsultantEmptyStateComponent } from './ai-consultant-empty-state/ai-consultant-empty-state.component';
import { AiConsultantMessageComponent } from './ai-consultant-message/ai-consultant-message.component';
import { AiConsultantSuggestedMessageComponent } from './ai-consultant-suggested-message/ai-consultant-suggested-message.component';

@UntilDestroy({ className: 'AiConsultantThreadComponent' })
@Component({
  standalone: true,
  imports: [
    AiConsultantMessageComponent,
    AiConsultantSuggestedMessageComponent,
    AiConsultantEmptyStateComponent,
    DatastoreAiConsultantModule,
    DatastoreAiConsultantChatTranscriptModule,
    CommonModule,
    TrackingModule,
    UiModule,
  ],
  selector: 'app-ai-consultant-thread',
  template: `
    <ng-container *ngIf="displayMessages.length > 0; else emptyState">
      <fl-scrollable-content
        #scrollableContent
        class="MessageSection"
        [noScrollBar]="true"
        [flMarginBottom]="'none'"
        [flMarginBottomTablet]="'xsmall'"
      >
        <app-ai-consultant-message
          *ngFor="let message of displayMessages; trackBy: trackByIndex"
          [message]="message"
          [messageAudioState]="
            message.type !== AiConsultantDisplayMessageType.LOADING &&
            message.content === playingMessage
              ? playingMessageSrc
                ? isPlaying
                  ? 'playing'
                  : 'stopped'
                : 'loading'
              : 'stopped'
          "
          [flMarginBottom]="'small'"
          (playAudio)="handlePlayAudio($event)"
          (pauseAudio)="handlePauseAudio()"
          (saveConversation)="sendChatTranscript()"
        >
        </app-ai-consultant-message>
      </fl-scrollable-content>
    </ng-container>
    <div class="InputSection">
      <fl-textarea
        #textarea
        class="InputSection-input"
        flTrackingSection="AiConsultantWidget-Thread"
        flTrackingLabel="MessageInputField"
        i18n-placeholder="AI Consultant chat input placeholder"
        placeholder="Message Ava..."
        [autoGrow]="true"
        [autoGrowMaxHeight]="125"
        [borderless]="true"
        [control]="control"
        [fontColor]="FontColor.LIGHT"
        [resize]="Resize.NONE"
        [rows]="1"
        [hideScrollbar]="true"
        (keydown)="handleKeydown($event)"
      ></fl-textarea>
      <fl-icon
        *ngIf="!isAiProcessing; else sendLoadingState"
        class="InputSection-button"
        flTrackingSection="AiConsultantWidget-Thread"
        flTrackingLabel="MessageSendButton"
        i18n-label="Send message label"
        label="Send"
        [clickable]="true"
        [color]="IconColor.LIGHT"
        [name]="'ui-send'"
        [size]="IconSize.MID"
        (click)="handleSend()"
        (mousedown)="keepTextareaFocused($event)"
      ></fl-icon>
    </div>
    <ng-template #emptyState>
      <fl-scrollable-content
        [noScrollBar]="true"
        [flMarginBottom]="'none'"
        [flMarginBottomTablet]="'xsmall'"
      >
        <app-ai-consultant-empty-state
          class="MessageSection"
          (suggestedMessageClicked)="handleSuggestedMessage($event)"
        >
        </app-ai-consultant-empty-state>
      </fl-scrollable-content>
    </ng-template>
    <ng-template #sendLoadingState>
      <fl-spinner
        flTrackingLabel="AiConsultantMessageSending"
        [color]="SpinnerColor.LIGHT"
        [size]="SpinnerSize.SMALL"
      ></fl-spinner>
    </ng-template>
  `,
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ai-consultant-thread.component.scss'],
})
export class AiConsultantThreadComponent implements OnInit {
  AiConsultantDisplayMessageType = AiConsultantDisplayMessageType;
  FontColor = FontColor;
  FontWeight = FontWeight;
  HrColor = HrColor;
  IconColor = IconColor;
  IconSize = IconSize;
  InputSize = InputSize;
  InputType = InputType;
  LoginOrSignup = LoginOrSignup;
  ModalSize = ModalSize;
  Resize = Resize;
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;
  TextSize = TextSize;
  trackByIndex = trackByIndex;

  control = new FormControl('', { nonNullable: true });

  messages: readonly AiConsultantMessage[] = [
    {
      role: AiConsultantMessageRole.ASSISTANT,
      content:
        "Hi I'm Ava, your AI guide to supercharging your business!\nWhether you're already running a business or dreaming of starting one, I'm here to help turn your vision into reality using AI powered freelancers. Share your business goals, and together, we'll create a project that our talented freelancers can bid on. Let's make your vision a reality!",
    },
  ];
  displayMessages: readonly (
    | AiConsultantMessageValid
    | AiConsultantMessageLoading
    | AiConsultantMessageDraftProject
  )[] = [];

  @Input() isPlaying: boolean;
  @Input() playingMessage?: string;
  @Input() playingMessageSrc?: string;

  @Output() openErrorToast = new EventEmitter<SaveConversationToastError>();
  @Output() playAudio = new EventEmitter<string>();
  @Output() pauseAudio = new EventEmitter<void>();

  @ViewChild('scrollableContent')
  scrollableContent: ScrollableContentComponent;

  isAiProcessing = false;
  MINIMUM_MESSAGE_TO_SAVE = 5;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private auth: Auth,
    private cd: ChangeDetectorRef,
    private datastore: Datastore,
    private datePipe: DatePipe,
    private localStorage: LocalStorage,
    private modalService: ModalService,
    private tracking: Tracking,
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      firstValueFrom(
        this.localStorage
          .get('aiConsultantChatHistory')
          .pipe(untilDestroyed(this)),
      ).then(chatHistory => {
        if (chatHistory) {
          this.messages = chatHistory;
          this.displayMessages = chatHistory.slice(1).map(message => {
            return this.parseIncomingMessage(message);
          });
          this.cd.markForCheck();
          this.scrollableContent?.scrollToBottom();
        }
      });
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key?.toLowerCase() === 'enter') {
      if (this.isAiProcessing) {
        return;
      }
      this.handleSend();
      event.preventDefault();
    }
  }

  handleSend(): void {
    if (!this.control.value) {
      return;
    }
    const newChat = this.control.value;
    this.displayMessages = [
      ...this.displayMessages,
      {
        type: AiConsultantDisplayMessageType.CHAT,
        role: AiConsultantMessageRole.USER,
        content: newChat,
      },
      // push message for thinking state
      {
        type: AiConsultantDisplayMessageType.LOADING,
        role: AiConsultantMessageRole.ASSISTANT,
      },
    ];
    this.scrollableContent?.scrollToBottom();
    this.control.reset();
    this.isAiProcessing = true;

    firstValueFrom(
      this.datastore
        .document<AiConsultantCollection>(
          'aiConsultant',
          this.sendMessage(newChat),
        )
        .valueChanges()
        .pipe(untilDestroyed(this)),
    ).then(newHistory => {
      this.tracking.trackCustomEvent('AI Consultant', 'AI Consultant Chat', {
        message: newChat,
        response: newHistory.history[newHistory.history.length - 1].content,
      } as AiConsultantTrackingExtraParams);

      const incomingMessageToDisplay = this.parseIncomingMessage(
        newHistory.history[newHistory.history.length - 1],
        newHistory.id,
      );

      this.messages = [
        ...this.messages,
        {
          type: AiConsultantDisplayMessageType.CHAT,
          role: AiConsultantMessageRole.USER,
          content: newChat,
        },
        {
          type: incomingMessageToDisplay.type,
          ...newHistory.history[newHistory.history.length - 1],
        },
      ];

      this.displayMessages = [
        ...this.displayMessages.slice(0, -1),
        incomingMessageToDisplay,
      ];
      this.isAiProcessing = false;
      this.cd.markForCheck();
      this.handlePauseAudio();
      this.scrollableContent?.scrollToBottom();
      this.localStorage.set('aiConsultantChatHistory', [...this.messages]);
    });
  }

  sendMessage(chat: string): Observable<string> {
    return from(
      this.datastore.collection<AiConsultantCollection>('aiConsultant').push({
        history: [
          ...this.messages,
          {
            content: chat,
            role: AiConsultantMessageRole.USER,
          },
        ],
      }),
    ).pipe(
      map(response => {
        if (response.status === 'success') {
          return response.id;
        }
        return AI_CONSULTANT_MESSAGE_EXCEPTION_ID;
      }),
    );
  }

  parseIncomingMessage(
    message: AiConsultantMessage,
    responseId?: string,
  ): AiConsultantMessageValid | AiConsultantMessageDraftProject {
    if (
      responseId === AI_CONSULTANT_MESSAGE_EXCEPTION_ID ||
      message.type === AiConsultantDisplayMessageType.ERROR
    ) {
      return {
        ...message,
        type: AiConsultantDisplayMessageType.ERROR,
      };
    }
    if (
      responseId === AI_CONSULTANT_MESSAGE_EXCEPTION_LIMIT_ID ||
      message.type === AiConsultantDisplayMessageType.LIMIT
    ) {
      return {
        ...message,
        type: AiConsultantDisplayMessageType.LIMIT,
      };
    }
    const projectData = this.getProjectDataIfExists(message.content);
    if (projectData) {
      return {
        role: AiConsultantMessageRole.ASSISTANT,
        type: AiConsultantDisplayMessageType.DRAFT_PROJECT,
        content: projectData.trimmedContent,
        title: projectData.title,
        description: projectData.description,
      };
    }

    return {
      role: message.role,
      content: message.content,
      type: AiConsultantDisplayMessageType.CHAT,
    };
  }

  getProjectDataIfExists(message: string): AiConsultantProjectData | undefined {
    const projectTitleAndDescriptionRegex =
      /PROJECT_TITLE\s*:\s*"((?:[^"]|")+)"\s*PROJECT_DESCR\s*:\s*"((?:[^"]|")+)"\s*.*/;
    const projectTitleAndDescription =
      projectTitleAndDescriptionRegex.exec(message);
    if (projectTitleAndDescription) {
      return {
        trimmedContent: `${message
          .replace(projectTitleAndDescriptionRegex, '')
          .trim()}\n\n`,
        title: projectTitleAndDescription[1],
        description: projectTitleAndDescription[2],
      };
    }
    return undefined;
  }

  handleSuggestedMessage(message: string): void {
    this.control.setValue(message);
    this.handleSend();
    this.control.reset();
  }

  // Keeps the textarea focused and prevents the virtual keyboard
  // from being closed on mobile when clicking outside the text area
  keepTextareaFocused(event: MouseEvent): void {
    event.preventDefault();
  }

  handlePlayAudio(message: string): void {
    this.playAudio.emit(message);
  }

  handlePauseAudio(): void {
    this.pauseAudio.emit();
  }

  async getChatHistoryStartIndex(): Promise<number> {
    const isLoggedIn = await firstValueFrom(
      this.auth.isLoggedIn().pipe(untilDestroyed(this)),
    );

    if (isLoggedIn) {
      // Send chat history from the start
      return 1;
    }

    const userId = await this.modalService
      .open('LoginSignupModal', {
        edgeToEdge: true,
        mobileFullscreen: true,
        inputs: {
          form: LoginOrSignup.SIGNUP,
          signupHeader: $localize`Sign up to save this conversation`,
          skipSuccessRedirectView: true,
        },
        size: ModalSize.XSMALL,
      })
      .afterClosed();

    if (userId) {
      const chatHistory = await firstValueFrom(
        this.localStorage
          .get('aiConsultantChatHistory')
          .pipe(untilDestroyed(this)),
      );
      let transcriptIndex = 1;

      // If user has previous chat history when logged in, append offline conversation
      if (chatHistory && chatHistory.length > 1) {
        // Send chat history from the start of offline conversation
        transcriptIndex = chatHistory.length;

        this.messages = [...chatHistory, ...this.messages.slice(1)];
        this.displayMessages = this.messages.slice(1).map(message => {
          return this.parseIncomingMessage(message);
        });
      }

      // Reset logged out chat history
      this.localStorage.set('aiConsultantChatHistory', null, true);
      // Set combined logged in and logged out chat history of user
      this.localStorage.set('aiConsultantChatHistory', [...this.messages]);

      this.cd.markForCheck();
      this.scrollableContent?.scrollToBottom();
      return transcriptIndex;
    }

    return 0;
  }

  async sendChatTranscript(): Promise<void> {
    if (this.displayMessages.length < this.MINIMUM_MESSAGE_TO_SAVE) {
      this.openErrorToast.emit(SaveConversationToastError.SHORT_HISTORY_ERROR);
    } else {
      const startIndex = await this.getChatHistoryStartIndex();
      if (startIndex < 1) {
        this.openErrorToast.emit(SaveConversationToastError.GENERIC_ERROR);
        return;
      }

      const emailModalRef = this.modalService.open(
        'AiConsultantEmailConfirmationModal',
        {
          closeable: false,
          mobileFullscreen: true,
          size: ModalSize.XSMALL,
        },
      );

      const result = await this.datastore
        .collection<AiConsultantChatTranscriptCollection>(
          'aiConsultantChatTranscript',
        )
        .push({
          history: this.messages.slice(startIndex).map(message => {
            return {
              content: message.content,
              role: message.role,
            };
          }),
          // new Date() will not be undefined and the null check
          // is used for the type checking
          dateLocaleString:
            this.datePipe.transform(new Date(), 'M/d/yyyy, h:mm:ss a') ?? '',
        });

      if (result.status === 'error') {
        this.modalService.close();

        emailModalRef.afterClosed().then(_ => {
          if (result.errorCode === 'TOO_MANY_REQUESTS') {
            this.openErrorToast.emit(
              SaveConversationToastError.RATE_LIMIT_ERROR,
            );
          } else {
            this.openErrorToast.emit(SaveConversationToastError.GENERIC_ERROR);
          }
        });
      }
    }
  }
}
